(function() {
  if (!window.AlgoliaConfig) {
    return;
  }

  const config = window.AlgoliaConfig;

  if (!config.appId || !config.apiKey) {
    return;
  }

  let articlesIndex = null;
  let initialized = false;
  let selectedFacets = {};
  let selectedNonFilteringFacets = {};
  let searchInput = null;
  let masterFacetValues = {};
  let currentFacetCounts = {};
  let sidebar = null;
  let mainContent = null;
  let paginationContainer = null;
  let currentPage = 0;
  let currentSortBy = 'featured';
  let _sortFallbackActive = false; /* true when sort replica failed and we are using featured + client-side sort */
  let _pendingScrollTop = false;   /* set to true before filter/sort calls so displayArticles scrolls back to top */
  const ITEMS_PER_PAGE = 6;
  const ART_MOBILE_BP = 1024;
  let _artDrawerOpen = false;

  /* Friendly display names for known facet keys.
     Any key not listed here gets an auto-generated name from its last segment. */
  const facetDisplayNames = {
    'tags':                    'Tags',
    'meta.custom.by_concern':  'By Concern',
    'meta.custom.by_category': 'By Category'
  };

  function getFacetDisplayName(key) {
    if (facetDisplayNames[key]) return facetDisplayNames[key];
    /* Auto-generate: take last dot-segment, replace underscores/hyphens, title-case */
    const segment = key.split('.').pop().replace(/[_-]/g, ' ');
    return segment.replace(/\b\w/g, c => c.toUpperCase());
  }

  /* Scroll to the top of the page — used after filter/sort interactions. */
  function scrollToArticlesTop() {
    setTimeout(function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 60);
  }

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  function createElement(tag, className, textContent = '') {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (textContent !== '' && textContent !== null && textContent !== undefined) el.textContent = textContent;
    return el;
  }

  function resetFiltersAndPage() {
    selectedFacets = {};
    selectedNonFilteringFacets = {};
    currentPage = 0;
  }

  function mergeFacetValues(fn, values) {
    if (!masterFacetValues[fn]) {
      masterFacetValues[fn] = values.slice();
      return;
    }
    values.forEach(v => {
      if (!masterFacetValues[fn].includes(v)) masterFacetValues[fn].push(v);
    });
  }

  function updateAlgoliaIndex(indexName) {
    if (!indexName) {
      return;
    }
    const client = algoliasearch(config.appId, config.apiKey);
    articlesIndex = client.initIndex(indexName);
  }

  function removeFacetSelection(store, facetName, facetValue) {
    if (!store[facetName]?.includes(facetValue)) return false;
    store[facetName] = store[facetName].filter(v => v !== facetValue);
    if (store[facetName].length === 0) delete store[facetName];
    return true;
  }

  function addFacetSelection(facetName, facetValue) {
    const count = currentFacetCounts?.[facetName]?.[facetValue] || 0;
    const store = count === 0 ? selectedNonFilteringFacets : selectedFacets;
    if (!store[facetName]) store[facetName] = [];
    store[facetName].push(facetValue);
  }

  function toggleFacet(facetName, facetValue) {
    // Remove from either set if already present
    const wasSelectedFiltering = removeFacetSelection(selectedFacets, facetName, facetValue);
    const wasSelectedNonFiltering = removeFacetSelection(selectedNonFilteringFacets, facetName, facetValue);

    // Only add if it wasn't already selected (true toggle behavior)
    if (!wasSelectedFiltering && !wasSelectedNonFiltering) {
      addFacetSelection(facetName, facetValue);
    }

    currentPage = 0;
    _pendingScrollTop = true;
    displayArticles();
  }

  function buildFacetFilters() {
    const filters = [];
    Object.keys(selectedFacets).forEach(facet => {
      selectedFacets[facet].forEach(value => {
        filters.push(`${facet}:${value}`);
      });
    });
    return filters;
  }

  function isMobileArt() { return window.innerWidth <= ART_MOBILE_BP; }

  function openArticlesDrawer() {
    if (!sidebar) return;
    _artDrawerOpen = true;
    sidebar.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const bd = document.querySelector('.articles-drawer-backdrop');
    if (bd) {
      bd.style.display = 'block';
      requestAnimationFrame(() => { bd.style.opacity = '1'; });
    }
  }

  function closeArticlesDrawer() {
    if (!sidebar) return;
    _artDrawerOpen = false;
    sidebar.classList.remove('is-open');
    document.body.style.overflow = '';
    const bd = document.querySelector('.articles-drawer-backdrop');
    if (bd) {
      bd.style.opacity = '0';
      setTimeout(() => { if (!_artDrawerOpen) bd.style.display = 'none'; }, 300);
    }
  }

  function ensureArticlesTabBar(panel) {
    /* Only on mobile; remove if viewport grows past breakpoint */
    const existing = panel.querySelector('.articles-tabbar');
    if (!isMobileArt()) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return; /* already injected */

    /* Backdrop */
    if (!document.querySelector('.articles-drawer-backdrop')) {
      const bd = document.createElement('div');
      bd.className = 'articles-drawer-backdrop';
      bd.addEventListener('click', closeArticlesDrawer);
      document.body.appendChild(bd);
    }

    /* Tabbar */
    const tabbar = document.createElement('div');
    tabbar.className = 'articles-tabbar';

    /* Filters button */
    const filterBtn = document.createElement('button');
    filterBtn.type = 'button';
    filterBtn.className = 'articles-tabbar__filter-btn';
    filterBtn.setAttribute('aria-label', 'Open filters');
    filterBtn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/>' +
      '</svg>Filters';
    filterBtn.addEventListener('click', () => {
      _artDrawerOpen ? closeArticlesDrawer() : openArticlesDrawer();
    });

    /* Sort wrapper — clone the desktop sort select into a tabbar pill */
    const sortWrap = document.createElement('div');
    sortWrap.className = 'olay-sort-wrapper';
    const sortLabel = document.createElement('span');
    sortLabel.className = 'olay-sort-label';
    sortLabel.textContent = 'Sort by';
    sortWrap.appendChild(sortLabel);

    /* Re-use (move) the existing articles-sort-select if it exists */
    let sortSelect = panel.querySelector('.articles-sort-select');
    if (sortSelect) {
      /* Detach from sort-row, re-attach inside tabbar */
      const clone = sortSelect.cloneNode(true);
      clone.addEventListener('change', (e) => {
        currentSortBy = e.target.value;
        _sortFallbackActive = false;
        const mobileSortIndexName = config.indices[currentSortBy];
        if (!mobileSortIndexName) {
          return;
        }
        updateAlgoliaIndex(mobileSortIndexName);
        currentPage = 0;
        _pendingScrollTop = true;
        displayArticles();
        /* Keep desktop select in sync */
        if (sortSelect) sortSelect.value = e.target.value;
      });
      sortWrap.appendChild(clone);
    }

    tabbar.appendChild(filterBtn);
    tabbar.appendChild(sortWrap);

    /* Inject tabbar at top of the results block, above sort-row */
    const sortRow = mainContent && mainContent.querySelector('.articles-sort-row');
    if (sortRow) {
      mainContent.insertBefore(tabbar, sortRow);
    } else if (mainContent) {
      mainContent.insertBefore(tabbar, mainContent.firstChild);
    }

    /* Set up drawer chrome inside sidebar if not done yet */
    setupArticlesDrawer();
  }

  function setupArticlesDrawer() {
    if (!sidebar || sidebar.querySelector('.olay-drawer-header')) return;

    /* Make sidebar a flex column */
    sidebar.style.setProperty('display', 'flex', 'important');
    sidebar.style.setProperty('flex-direction', 'column', 'important');

    /* Header */
    const header = document.createElement('div');
    header.className = 'olay-drawer-header';
    const title = document.createElement('span');
    title.className = 'olay-drawer-title';
    title.textContent = 'Filters';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'olay-drawer-close';
    closeBtn.setAttribute('aria-label', 'Close filters');
    closeBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    closeBtn.addEventListener('click', closeArticlesDrawer);
    header.appendChild(title);
    header.appendChild(closeBtn);

    /* Body — wrap existing sidebar content */
    const body = document.createElement('div');
    body.className = 'olay-drawer-body';
    while (sidebar.firstChild) {
      body.appendChild(sidebar.firstChild);
    }

    /* Footer */
    const footer = document.createElement('div');
    footer.className = 'olay-drawer-footer';
    const footerRow = document.createElement('div');
    footerRow.className = 'olay-drawer-footer-row';
    const viewBtn = document.createElement('button');
    viewBtn.type = 'button';
    viewBtn.className = 'olay-drawer-view-results';
    viewBtn.textContent = 'VIEW RESULTS';
    viewBtn.addEventListener('click', closeArticlesDrawer);
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'olay-drawer-clear';
    clearBtn.setAttribute('aria-label', 'Clear all filters');
    clearBtn.textContent = 'Clear all';
    clearBtn.addEventListener('click', () => {
      selectedFacets = {};
      selectedNonFilteringFacets = {};
      currentPage = 0;
      _pendingScrollTop = true;
      displayArticles();
    });
    footerRow.appendChild(viewBtn);
    footerRow.appendChild(clearBtn);
    footer.appendChild(footerRow);

    sidebar.appendChild(header);
    sidebar.appendChild(body);
    sidebar.appendChild(footer);
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    if (initialized) return;

    if (!window.algoliasearch) {
      /* CDN hasn't loaded yet — retry in 200 ms (max 5 s) */
      if ((init._retries = (init._retries || 0) + 1) <= 25) {
        setTimeout(init, 200);
      }
      return;
    }

    initialized = true;

    updateAlgoliaIndex(config.indices.featured);

    const articlesPanel = document.querySelector('[data-panel="articles"]');
    if (!articlesPanel) {
      return;
    }

    createPanelLayout(articlesPanel);

    /* Bind to the shared search bar above the tabs */
    searchInput = document.querySelector('.olay-shared-search__input');
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const panel = document.querySelector('[data-panel="articles"]');
        if (panel && !panel.hidden) {
          currentPage = 0;
          displayArticles();
        }
      });
    }

    /* Refresh whenever the articles panel becomes visible (tab switch).
       This handles the case where the user typed a query while on the
       products tab — the input event was skipped because the panel was hidden. */
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'hidden' && !articlesPanel.hidden) {
          currentPage = 0;
          displayArticles();
        }
      });
    });
    observer.observe(articlesPanel, { attributes: true, attributeFilter: ['hidden'] });

    /* Only fire immediately if the articles panel is already visible (e.g. ?tab=articles
       in the URL and algolia-tabs.js ran first). If the panel is still hidden (products tab
       is active), skip — the MutationObserver above will call displayArticles() with the
       correct search query once the user switches tabs. */
    if (!articlesPanel.hidden) {
      displayArticles();
    }
  }

  function createPanelLayout(panel) {
    if (panel.querySelector('#articles-facets-sidebar')) {
      return;
    }

    panel.innerHTML = '';

    /* Grid wrapper — mirrors .ais-page layout from algolia-search-styles */
    const wrapper = createElement('div', 'articles-wrapper');

    /* ── SIDEBAR (grid-area: sidebar) ────────────────────── */
    sidebar = createElement('div', 'articles-sidebar');
    sidebar.id = 'articles-facets-sidebar';
    wrapper.appendChild(sidebar);

    /* ── RESULTS BLOCK (grid-area: results) ──────────────── */
    mainContent = createElement('div', 'articles-block');
    mainContent.id = 'articles-main-content';

    /* Sort bar — right-aligned, matching products sort */
    const sortContainer = createElement('div', 'articles-sort-row');
    initSortBy(sortContainer);
    mainContent.appendChild(sortContainer);

    /* Article cards */
    const resultsContainer = createElement('div', 'ais-Hits-list-articles');
    resultsContainer.setAttribute('data-articles-results', '');
    mainContent.appendChild(resultsContainer);

    paginationContainer = createElement('div', 'articles-pagination-container');
    mainContent.appendChild(paginationContainer);

    wrapper.appendChild(mainContent);
    panel.appendChild(wrapper);

    /* Inject mobile tabbar if needed right away */
    ensureArticlesTabBar(panel);

    /* Re-check on resize (e.g. rotate phone, resize browser) */
    window.addEventListener('resize', () => ensureArticlesTabBar(panel), { passive: true });
  }

  // ============================================
  // FILTERS & FACETS FUNCTIONALITY
  // ============================================
  function renderFacetsHeader(target, drawerBody) {
    if (!drawerBody) {
      const filtersHeader = createElement('div', 'olay-sidebar-header');
      filtersHeader.innerHTML =
        '<svg class="olay-sidebar-header__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/>' +
        '</svg>' +
        '<span class="olay-sidebar-header__title">Filters</span>';

      if (Object.keys(selectedFacets).length > 0) {
        renderClearAllButton(filtersHeader);
      }
      target.appendChild(filtersHeader);
      return;
    }

    /* Drawer: show/hide clear-all inside drawer header */
    const drawerHeader = sidebar.querySelector('.olay-drawer-header');
    if (!drawerHeader) return;
    const existingClear = drawerHeader.querySelector('.articles-clear-all-btn');
    if (Object.keys(selectedFacets).length > 0 && !existingClear) {
      renderClearAllButton(drawerHeader);
    } else if (Object.keys(selectedFacets).length === 0 && existingClear) {
      existingClear.remove();
    }
  }

  function promoteQualifyingNonFilteringFacets(facets) {
    /* If any previously-selected non-filtering facets now have a positive count,
       promote them to real filtering selections so they affect results going forward. */
    Object.keys(selectedNonFilteringFacets).forEach(fn => {
      selectedNonFilteringFacets[fn].forEach(v => {
        const nowCount = facets?.[fn]?.[v] || 0;
        if (nowCount === 0) return;
        // move to selectedFacets
        if (!selectedFacets[fn]) selectedFacets[fn] = [];
        if (!selectedFacets[fn].includes(v)) selectedFacets[fn].push(v);
        // remove from non-filtering
        selectedNonFilteringFacets[fn] = selectedNonFilteringFacets[fn].filter(x => x !== v);
      });
      if (selectedNonFilteringFacets[fn] && selectedNonFilteringFacets[fn].length === 0) delete selectedNonFilteringFacets[fn];
    });
  }

  function renderFacets(facets) {
    if (!sidebar) return;

    /* On mobile the sidebar is a drawer — render content inside .olay-drawer-body.
       On desktop write directly into sidebar (even if drawer chrome is present from a prior
       mobile viewport — we detect by isMobileArt() not just DOM presence). */
    const drawerBody = isMobileArt() ? sidebar.querySelector('.olay-drawer-body') : null;
    const target = drawerBody || sidebar;

    target.innerHTML = '';

    /* Sidebar header — matches products .olay-sidebar-header (icon + "Filters").
       On mobile this is the drawer chrome header (already in DOM); on desktop render it here. */
    renderFacetsHeader(target, drawerBody);

    if (Object.keys(selectedFacets).length > 0) {
      renderSelectedFilters(target);
    }

    /* Capture/merge a master list of facet values so filters can remain visible
       even when Algolia omits zero-count options after applying facetFilters. */
    if (facets && Object.keys(facets).length > 0) {
      if (Object.keys(selectedFacets).length === 0 && Object.keys(masterFacetValues).length === 0) {
        Object.keys(facets).forEach(fn => {
          masterFacetValues[fn] = Object.keys(facets[fn]);
        });
      } else {
        Object.keys(facets).forEach(fn => {
          mergeFacetValues(fn, Object.keys(facets[fn]));
        });
      }
    }

    promoteQualifyingNonFilteringFacets(facets);

    /* Only render filter groups if Algolia returned facet data */
    if (facets && Object.keys(facets).length > 0) {
      renderFacetOptions(facets, target, masterFacetValues);
    }
  }

  function renderClearAllButton(headerWrapper) {
    const btn = createElement('button', 'articles-clear-all-btn', 'Clear All');
    btn.onclick = (e) => {
      e.preventDefault();
      resetFiltersAndPage();
      _pendingScrollTop = true;
      displayArticles();
    };
    /* Insert before the close button so order is: Title | Clear All | ✕ */
    const closeBtn = headerWrapper.querySelector('.olay-drawer-close');
    if (closeBtn) {
      headerWrapper.insertBefore(btn, closeBtn);
    } else {
      headerWrapper.appendChild(btn);
    }
  }

  function buildFilterListItem(facetName, value, isNonFiltering) {
    const listItem = document.createElement('li');
    listItem.className = isNonFiltering ? 'articles-filter-item non-filtering' : 'articles-filter-item';

    const link = document.createElement('a');
    link.href = '#';
    link.className = 'articles-filter-link';
    link.setAttribute('data-facet', facetName);
    link.setAttribute('data-value', value);

    const contentDiv = createElement('div', 'articles-filter-content');
    const labelDiv = createElement('div', 'articles-filter-label', facetDisplayNames[facetName] || facetName);
    contentDiv.appendChild(labelDiv);

    const valueSpan = document.createElement('span');
    const displayValue = value && value.length ? value.charAt(0).toUpperCase() + value.slice(1) : value;
    valueSpan.textContent = ': ' + displayValue;
    contentDiv.appendChild(valueSpan);

    link.appendChild(contentDiv);

    const removeBtn = createElement('button', 'articles-filter-remove', '\u2715');
    removeBtn.onclick = (e) => {
      e.preventDefault();
      toggleFacet(facetName, value);
    };
    link.appendChild(removeBtn);

    listItem.appendChild(link);
    return listItem;
  }

  function renderSelectedFilters(target) {
    if (!target) target = sidebar;  /* fallback */
    const container = createElement('div', 'articles-selected-filters');
    const filterList = document.createElement('ul');
    filterList.className = 'articles-filter-list';

    // Render both filtering and non-filtering applied selections so they show above the facets
    Object.keys(selectedFacets).forEach(facetName => {
      selectedFacets[facetName].forEach(value => {
        filterList.appendChild(buildFilterListItem(facetName, value, false));
      });
    });

    container.appendChild(filterList);
    // Also render non-filtering selections (count was zero when selected)
    Object.keys(selectedNonFilteringFacets).forEach(facetName => {
      selectedNonFilteringFacets[facetName].forEach(value => {
        filterList.appendChild(buildFilterListItem(facetName, value, true));
      });
    });

    target.appendChild(container);
  }

  function renderFacetOptions(facets, target, masterValues = {}) {
    if (!target) target = sidebar;  /* fallback */
    /* Build the full set of facet keys to render using config order, plus
       any keys returned by Algolia and any previously discovered master values. */
    const allKeys = new Set([...(config.facets || []), ...Object.keys(facets || {}), ...Object.keys(masterValues || {})]);
    const facetsToRender = Array.from(allKeys);

    const fragment = document.createDocumentFragment();

    facetsToRender.forEach(facetName => {
      if (!facets[facetName] && !(masterValues && masterValues[facetName])) return;

      const group = createElement('div', 'articles-facet-group');
      const displayName = getFacetDisplayName(facetName);

      const groupHeader = createElement('div', 'articles-facet-group-header');

      const title = createElement('h5', 'articles-facet-title', displayName);
      groupHeader.appendChild(title);

      const toggleBtn = createElement('button', 'articles-facet-toggle', '-');
      toggleBtn.setAttribute('aria-expanded', 'true');
      
      const optionsWrapper = createElement('div', 'articles-facet-options');
      
      toggleBtn.onclick = (e) => {
        e.preventDefault();
        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        toggleBtn.setAttribute('aria-expanded', !isExpanded);
        toggleBtn.textContent = isExpanded ? '+' : '-';
        optionsWrapper.classList.toggle('collapsed');
      };

      groupHeader.appendChild(toggleBtn);
      group.appendChild(groupHeader);

      const valuesToIterate = (masterValues && masterValues[facetName] && masterValues[facetName].length)
        ? masterValues[facetName]
        : Object.keys(facets[facetName] || {});

      valuesToIterate.forEach(facetValue => {
        const count = (facets && facets[facetName] && typeof facets[facetName][facetValue] !== 'undefined')
          ? facets[facetName][facetValue]
          : 0;
        const isSelected = (selectedFacets[facetName] && selectedFacets[facetName].includes(facetValue))
          || (selectedNonFilteringFacets[facetName] && selectedNonFilteringFacets[facetName].includes(facetValue))
          || false;

        const label = createElement('label', 'articles-facet-label');

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'articles-facet-checkbox';
        checkbox.value = facetValue;
        checkbox.dataset.facet = facetName;
        checkbox.checked = isSelected;

        checkbox.onchange = () => {
          toggleFacet(facetName, facetValue);
        };

        label.appendChild(checkbox);

        const labelTextContainer = createElement('div', 'articles-facet-label-text');
        const facetValueSpan = createElement('span', 'articles-facet-value', facetValue);
        const countSpan = createElement('span', 'articles-facet-count', count);

        labelTextContainer.appendChild(facetValueSpan);
        labelTextContainer.appendChild(countSpan);

        labelTextContainer.onclick = (e) => {
          e.stopPropagation();
          toggleFacet(facetName, facetValue);
        };
        label.appendChild(labelTextContainer);

        optionsWrapper.appendChild(label);
      });

      group.appendChild(optionsWrapper);
      fragment.appendChild(group);
    });

    target.appendChild(fragment);
  }

  // ============================================
  // SEARCH INPUT FUNCTIONALITY
  // ============================================
  function initSearchInput(searchSection) {
    const searchContainer = createElement('div', 'ais-search-box-container articles-search-container');
    searchContainer.innerHTML = `
      <div class="ais-search-box">
        <form novalidate class="ais-search-box--form">
          <input class="ais-search-box--input articles-search-input" type="search" placeholder="Search for articles" autocomplete="off" spellcheck="false">
          <button class="ais-search-box--submit" type="submit" aria-label="Search"></button>
          <button class="ais-search-box--reset" type="reset" style="display: none;"></button>
        </form>
      </div>
    `;
    searchSection.appendChild(searchContainer);

    searchInput = searchContainer.querySelector('.ais-search-box--input');

    searchInput.addEventListener('input', () => {
      currentPage = 0;
      displayArticles();
    });
  }

  // ============================================
  // SORT BY FUNCTIONALITY
  // ============================================
  function initSortBy(sortRow) {
    const sortContainer = createElement('div', 'articles-sort-container');

    const selectWrapper = createElement('div', 'olay-sort-wrapper');
    const sortLabel = createElement('span', 'olay-sort-label', 'Sort by');
    selectWrapper.appendChild(sortLabel);

    const sortSelect = document.createElement('select');
    sortSelect.id = 'articles-sort-by';
    sortSelect.className = 'articles-sort-select';
    sortSelect.innerHTML = `
      <option value="featured">Featured</option>
      <option value="z_a">Z - A</option>
      <option value="a_z">A - Z</option>
    `;
    sortSelect.onchange = (e) => {
      currentSortBy = e.target.value;
      _sortFallbackActive = false;
      const sortIndexName = config.indices[currentSortBy];
      if (!sortIndexName) {
        return;
      }
      updateAlgoliaIndex(sortIndexName);
      currentPage = 0;
      _pendingScrollTop = true;
      displayArticles();
    };

    selectWrapper.appendChild(sortSelect);
    sortContainer.appendChild(selectWrapper);
    sortRow.appendChild(sortContainer);
  }

  // ============================================
  // ARTICLE CARDS & PAGINATION FUNCTIONALITY
  // ============================================

  /* When using client-side sort fallback, fetch all results in a single request
     so we can sort globally before paginating — otherwise later pages can still
     contain articles whose titles start with 'A' even in an A-Z sort. */
  async function fetchAndRenderWithClientSort(query, facetFilters) {
    const allResults = await articlesIndex.search(query, {
      page: 0,
      hitsPerPage: 1000,
      facets: ['*'],
      facetFilters: facetFilters.length ? facetFilters : undefined
    });

    const allHits = allResults.hits.slice();
    if (currentSortBy === 'a_z') {
      allHits.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (currentSortBy === 'z_a') {
      allHits.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    }

    const totalHits = allHits.length;
    const totalPages = Math.ceil(totalHits / ITEMS_PER_PAGE) || 1;
    const pageHits = allHits.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

    currentFacetCounts = allResults.facets || {};
    renderFacets(allResults.facets);
    renderResults(pageHits, currentPage, totalPages, totalHits, allResults.processingTimeMS);
  }

  async function fetchAndRenderNormal(query, facetFilters) {
    const results = await articlesIndex.search(query, {
      page: currentPage,
      hitsPerPage: ITEMS_PER_PAGE,
      facets: ['*'],
      facetFilters: facetFilters.length ? facetFilters : undefined
    });

    currentFacetCounts = results.facets || {};
    renderFacets(results.facets);
    renderResults(results.hits, results.page, results.nbPages, results.nbHits, results.processingTimeMS);
  }

  function handleDisplayArticlesError(page, doScrollTop, resultsContainer) {
    /* If a sort replica index failed, fall back to featured index with client-side
       sort — dropdown stays on the user's selection, articles remain visible. */
    if (!_sortFallbackActive && currentSortBy !== 'featured' && config.indices.featured) {
      _sortFallbackActive = true;
      /* Re-arm scroll so it still fires after the fallback renders */
      if (doScrollTop) _pendingScrollTop = true;
      updateAlgoliaIndex(config.indices.featured);
      displayArticles(page);
    } else if (resultsContainer) {
      resultsContainer.innerHTML =
        '<p class="articles-no-results">Could not load articles. Please check the Algolia index settings.</p>';
    }
  }

  async function displayArticles(page = 0) {
    if (!articlesIndex || !mainContent) return;

    currentPage = page;

    /* Capture and clear the scroll flag before any await so concurrent calls
       don't accidentally consume each other's flag. */
    const doScrollTop = _pendingScrollTop;
    _pendingScrollTop = false;

    const resultsContainer = mainContent.querySelector('[data-articles-results]');

    try {
      /* Use shared input value; fall back to URL ?q= so the initial call
       picks up the search term even if the input hasn't been filled yet. */
      const query = searchInput?.value ||
        (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('q') || '' : '');
      const facetFilters = buildFacetFilters();

      if (_sortFallbackActive && currentSortBy !== 'featured') {
        await fetchAndRenderWithClientSort(query, facetFilters);
      } else {
        await fetchAndRenderNormal(query, facetFilters);
      }

      if (doScrollTop) scrollToArticlesTop();
    } catch {
      handleDisplayArticlesError(page, doScrollTop, resultsContainer);
    }
  }

  function renderResults(articles, currentPageNum = 0, totalPages = 1, nbHits = 0, processingTimeMS = 0) {
    const container = mainContent.querySelector('[data-articles-results]');
    if (!container) return;

    renderStats(currentPageNum, nbHits, processingTimeMS);

    if (!articles.length) {
      container.innerHTML =
        '<div class="articles-no-results-state">' +
          '<p class="articles-no-results">No result found</p>' +
          '<p class="articles-no-results-sub">Try clearing the filters or changing your input</p>' +
        '</div>';
      if (paginationContainer) paginationContainer.innerHTML = '';
      return;
    }

    renderArticleCards(articles, container);
    renderPagination(paginationContainer, currentPageNum, totalPages);
  }

  function renderStats(currentPageNum, nbHits, processingTimeMS) {
    let statsEl = mainContent.querySelector('.articles-stats');
    if (!statsEl) {
      statsEl = createElement('div', 'articles-stats');
      /* Place INSIDE the sort row as the first child (left side),
         sort container stays on the right — mirrors .ais-search-header layout */
      const sortRow = mainContent.querySelector('.articles-sort-row');
      if (sortRow) {
        sortRow.insertBefore(statsEl, sortRow.firstChild);
      } else {
        mainContent.insertBefore(statsEl, mainContent.firstChild);
      }
    }
    if (nbHits === 0) {
      const secs = (processingTimeMS / 1000).toFixed(3);
      statsEl.textContent = `No result found in ${secs}s`;
      return;
    }
    const from = currentPageNum * ITEMS_PER_PAGE + 1;
    const to = Math.min((currentPageNum + 1) * ITEMS_PER_PAGE, nbHits);
    const secs = (processingTimeMS / 1000).toFixed(3);
    statsEl.innerHTML =
      `${from} – ${to} out of <span class="articles-stats__count">${nbHits}</span> results found in ${secs}s`;
  }

  function renderArticleCards(articles, container) {
    container.innerHTML = articles.map((article) => {
      const bodyText = article.body_html ? article.body_html.replace(/<[^<>]*>/g, '').trim() : '';
      const trimmedBody = bodyText.length > 77 ? bodyText.slice(0, 77) + '...' : bodyText;
      
      const imageUrl = typeof article.image === 'string' ? article.image : article.image?.url || '';
      const articleUrl = `/blogs/${config.blogHandle}/${article.handle}`;
      const imageHtml = imageUrl ? `<a href="${articleUrl}" class="article-image-link"><img src="${imageUrl}" alt="${article.title}"></a>` : '';

      return `
        <div class="articles-card">
          <div class="article-image-container">
            ${imageHtml}
            <a href="${articleUrl}">
              <h3 class="article-heading">${article.title}</h3>
            </a>
          </div>
          <div class="article-content">
            <p class="article-description">${trimmedBody}</p>
            <a href="${articleUrl}" class="article-readmore-block">Read More</a>
          </div>
        </div>
      `;
    }).join('');

    equalizeHeadingHeights(container);
  }

  function equalizeHeadingHeights(container) {
    setTimeout(() => {
      const cards = container.querySelectorAll('.articles-card');
      const cardsPerRow = 3;

      for (let i = 0; i < cards.length; i += cardsPerRow) {
        const rowCards = Array.from(cards).slice(i, i + cardsPerRow);
        const headings = rowCards.map(card => card.querySelector('.article-heading'));

        const maxHeight = Math.max(...headings.map(h => h.offsetHeight));

        headings.forEach(heading => {
          heading.style.height = maxHeight + 'px';
        });
      }
    }, 0);
  }

  function createPaginationButton(text, isDisabled, onClick) {
    const btn = createElement('button', 'articles-pagination-btn', text);
    btn.disabled = isDisabled;
    if (!isDisabled) {
      btn.onclick = onClick;
    }
    return btn;
  }

  function createPaginationItem(label, pageNum, isSelected, isDisabled, onClick) {
    const item = document.createElement('li');
    item.className = 'ais-Pagination-item';
    if (pageNum === null) {
      item.classList.add(label === 'Previous' ? 'ais-Pagination-item--previousPage' : 'ais-Pagination-item--nextPage');
    }
    
    if (isSelected) item.classList.add('ais-Pagination-item--selected');
    if (isDisabled) item.classList.add('ais-Pagination-item--disabled');
    
    const link = document.createElement('a');
    link.className = 'ais-Pagination-link';
    if (isSelected) link.classList.add('ais-Pagination-link--selected');
    
    link.href = '#';
    link.textContent = label;
    link.onclick = (e) => {
      e.preventDefault();
      if (!isDisabled) onClick();
    };
    
    item.appendChild(link);
    return item;
  }

  function renderPagination(container, currentPageNum, totalPages) {
    container.innerHTML = '';
    const list = document.createElement('ul');
    list.className = 'ais-Pagination-list';

    // Previous
    list.appendChild(createPaginationItem(
      'Previous',
      null,
      false,
      currentPageNum === 0,
      () => displayArticles(currentPageNum - 1)
    ));

    // Page Numbers
    for (let i = 0; i < totalPages; i++) {
      list.appendChild(createPaginationItem(
        i + 1,
        i,
        i === currentPageNum,
        false,
        () => displayArticles(i)
      ));
    }

    // Next
    list.appendChild(createPaginationItem(
      'Next >',
      null,
      false,
      currentPageNum >= totalPages - 1,
      () => displayArticles(currentPageNum + 1)
    ));

    container.appendChild(list);
  }

  // ============================================
  // AUTO-INITIALIZATION
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.articlesDebug = () => ({
    config,
    initialized,
    hasIndex: !!articlesIndex,
    selectedFacets
  });
})();
